import React, { useState } from 'react';
import { 
    Box, AppBar, Toolbar, Typography, IconButton, Chip, 
    Button, Tooltip, Avatar, useTheme, useMediaQuery, Stack
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { authService } from '../../services/auth';

const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout failed:', error);
            window.location.href = '/login';
        }
    };

    return (
        <Box sx={{ 
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
            position: 'relative',
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)'
        }}>
            <AppBar 
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.92)',
                    backdropFilter: 'blur(16px)',
                    borderBottom: '1px solid #E2E8F0',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.02)',
                }}
            >
                <Toolbar sx={{ minHeight: { xs: 58, sm: 64 }, px: { xs: 1.5, sm: 3 } }}>
                    <IconButton
                        color="primary"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        edge="start"
                        sx={{ 
                            mr: { xs: 1, sm: 2 },
                            backgroundColor: 'rgba(79, 70, 229, 0.06)',
                            '&:hover': {
                                backgroundColor: 'rgba(79, 70, 229, 0.12)',
                            }
                        }}
                    >
                        <MenuIcon fontSize="small" />
                    </IconButton>

                    {/* Logo & Brand */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.25, 
                            cursor: 'pointer',
                            userSelect: 'none'
                        }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <Avatar
                            sx={{
                                width: 34,
                                height: 34,
                                bgcolor: 'primary.main',
                                background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                                fontWeight: 800,
                                fontSize: '0.875rem',
                                boxShadow: '0 2px 8px rgba(79, 70, 229, 0.35)'
                            }}
                        >
                            GC
                        </Avatar>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        fontWeight: 800, 
                                        fontSize: { xs: '1.05rem', sm: '1.15rem' },
                                        color: '#0F172A',
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1.2
                                    }}
                                >
                                    GET·CODE
                                </Typography>
                                <Chip 
                                    label="PRO" 
                                    size="small" 
                                    sx={{ 
                                        height: 18, 
                                        fontSize: '0.65rem', 
                                        fontWeight: 800, 
                                        backgroundColor: '#4F46E5', 
                                        color: '#fff',
                                        letterSpacing: '0.05em',
                                        px: 0.3
                                    }} 
                                />
                            </Box>
                            {!isMobile && (
                                <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: -0.2 }}>
                                    Onboarding & Operations Console
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Quick navigation pill links (Desktop) */}
                    {!isMobile && (
                        <Stack direction="row" spacing={1} sx={{ ml: 4 }}>
                            <Button
                                size="small"
                                startIcon={<GroupAddIcon />}
                                onClick={() => navigate('/onboarding')}
                                sx={{
                                    color: location.pathname === '/onboarding' ? 'primary.main' : 'text.secondary',
                                    fontWeight: location.pathname === '/onboarding' ? 700 : 500,
                                    backgroundColor: location.pathname === '/onboarding' ? 'primary.light' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'primary.light',
                                    }
                                }}
                            >
                                Onboarding
                            </Button>
                            <Button
                                size="small"
                                startIcon={<DashboardIcon />}
                                onClick={() => navigate('/dashboard')}
                                sx={{
                                    color: location.pathname === '/dashboard' ? 'primary.main' : 'text.secondary',
                                    fontWeight: location.pathname === '/dashboard' ? 700 : 500,
                                    backgroundColor: location.pathname === '/dashboard' ? 'primary.light' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'primary.light',
                                    }
                                }}
                            >
                                Dashboard
                            </Button>
                        </Stack>
                    )}

                    <Box sx={{ flexGrow: 1 }} />

                    {/* Scout Status Pill */}
                    <Tooltip title="In-House AI Scouting Agent active and ready (OpenAI + Telegram Bot)">
                        <Chip
                            icon={<AutoAwesomeIcon style={{ color: '#059669', fontSize: 16 }} />}
                            label={isMobile ? "AI Scout" : "Scout Agent Active"}
                            size="small"
                            sx={{
                                backgroundColor: '#ECFDF5',
                                color: '#065F46',
                                border: '1px solid #A7F3D0',
                                fontWeight: 600,
                                mr: { xs: 1, sm: 2 },
                                '& .MuiChip-icon': { ml: 0.8 }
                            }}
                        />
                    </Tooltip>

                    {/* Logout Button */}
                    <Tooltip title="Log out of Admin Portal">
                        <IconButton 
                            onClick={handleLogout}
                            size="small"
                            sx={{
                                color: '#64748B',
                                border: '1px solid #E2E8F0',
                                borderRadius: '10px',
                                p: 0.8,
                                '&:hover': {
                                    backgroundColor: '#FEE2E2',
                                    color: '#DC2626',
                                    borderColor: '#FCA5A5'
                                }
                            }}
                        >
                            <LogoutIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 1.5, sm: 3 },
                    mt: { xs: 7.5, sm: 8.5 },
                    minHeight: 'calc(100vh - 68px)',
                    boxSizing: 'border-box',
                    maxWidth: 1600,
                    mx: 'auto',
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default Layout;